import React, { useState } from 'react';
import { Filter } from 'lucide-react';
import { portfolioProjects } from '../mock';
import { Button } from './ui/button';

const Portfolio = () => {
  const [activeFilter, setActiveFilter] = useState('All');

  const styles = ['All', 'Contemporary', 'French Provincial', 'Classic', 'Duplex'];

  const filteredProjects = activeFilter === 'All'
    ? portfolioProjects
    : portfolioProjects.filter(project => project.style === activeFilter);

  return (
    <section id="portfolio" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-block mb-4">
            <span className="px-4 py-2 bg-[#c9a961]/10 text-[#c9a961] rounded-full text-sm font-semibold">
              Our Portfolio
            </span>
          </div>
          
          <h2
            className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Exquisite Homes We've{' '}
            <span className="text-[#c9a961]">Crafted</span>
          </h2>
          
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Explore our collection of bespoke luxury homes across various architectural styles, each reflecting our commitment to excellence and attention to detail.
          </p>
        </div>

        {/* Filter Buttons */}
        <div className="flex flex-wrap items-center justify-center gap-3 mb-12">
          <div className="flex items-center gap-2 text-gray-600">
            <Filter className="h-5 w-5" />
            <span className="font-semibold">Filter by Style:</span>
          </div>
          {styles.map((style) => (
            <Button
              key={style}
              onClick={() => setActiveFilter(style)}
              variant={activeFilter === style ? 'default' : 'outline'}
              className={`transition-all ${
                activeFilter === style
                  ? 'bg-[#c9a961] hover:bg-[#b89851] text-white'
                  : 'border-gray-300 hover:border-[#c9a961] hover:text-[#c9a961]'
              }`}
            >
              {style}
            </Button>
          ))}
        </div>

        {/* Portfolio Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProjects.map((project) => (
            <div
              key={project.id}
              className="group bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
            >
              {/* Image */}
              <div className="relative h-64 overflow-hidden bg-gray-100">
                <img
                  src={project.image}
                  alt={project.title}
                  className={`w-full h-full ${project.imageStyle === 'contain' ? 'object-contain' : 'object-cover'} group-hover:scale-110 transition-transform duration-500`}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                {/* Style Badge */}
                <div className="absolute top-4 right-4">
                  <span className="px-3 py-1 bg-white/90 backdrop-blur-sm text-gray-900 rounded-full text-xs font-semibold">
                    {project.style}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-[#c9a961] transition-colors">
                  {project.title}
                </h3>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  {project.description}
                </p>
                
                {/* Details */}
                <div className="flex items-center justify-between text-sm">
                  <div className="text-gray-500">
                    <span className="font-semibold text-gray-900">{project.size}</span> • {project.year}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Note */}
        {filteredProjects.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No projects found for this style.</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default Portfolio;